'use client';

import * as React from 'react';
import { createContext, useContext, useId } from 'react';
import type { ReactNode } from 'react';
import { cx } from '../utils';

/* ═══════════════════════════════════════════════════
   Menu — shared menu surface primitive
   APG: Menu (https://www.w3.org/WAI/ARIA/apg/patterns/menu/)

   OWNERSHIP CONTRACT (see blueprint/components/17-menu.md §8):
   ┌────────────────────┬──────────────────────────────┐
   │ Menu OWNS          │ Consumer OWNS                │
   ├────────────────────┼──────────────────────────────┤
   │ role="menu"        │ Trigger + aria-haspopup      │
   │ Keyboard nav       │ Open/close state             │
   │ (Arrow/Home/End)   │ Positioning + portal         │
   │ Roving focus       │ Click-outside dismiss        │
   │ Entry animation    │ Focus return on close        │
   │ Item rendering     │ Item data (labels, actions)  │
   │ onClose callback   │ Handling onClose (Esc vs Tab)│
   └────────────────────┴──────────────────────────────┘
   ═══════════════════════════════════════════════════ */

// ── Context ──────────────────────────────────────────

interface MenuContextValue {
  onAction?: (value: string) => void;
  onClose?: (reason: 'escape' | 'tab' | 'select') => void;
}

const MenuContext = createContext<MenuContextValue>({});

// ── Menu (container) ─────────────────────────────────

export interface MenuProps {
  /** Called when Escape or Tab is pressed. Consumer handles focus return. */
  onClose?: (reason: 'escape' | 'tab' | 'select') => void;
  /** Called when a menu item is activated. */
  onAction?: (value: string) => void;
  /** Accessible label for the menu. */
  ariaLabel?: string;
  className?: string;
  /** Inline style — consumers use this for positioning (e.g. fixed portal). */
  style?: React.CSSProperties;
  children?: ReactNode;
}

/** Query enabled menuitem buttons inside a menu container. */
function getEnabledItems(container: HTMLElement): HTMLButtonElement[] {
  return Array.from(
    container.querySelectorAll<HTMLButtonElement>('button[role="menuitem"]:not(:disabled)'),
  );
}

export const Menu = React.forwardRef<HTMLDivElement, MenuProps>(function Menu(
  { onClose, onAction, ariaLabel, className, style, children },
  ref,
) {
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const container = e.currentTarget;
      const enabledItems = getEnabledItems(container);
      if (enabledItems.length === 0 && e.key !== 'Escape' && e.key !== 'Tab') return;

      const currentIndex = enabledItems.findIndex((el) => el === document.activeElement);

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const nextIndex = currentIndex < enabledItems.length - 1 ? currentIndex + 1 : 0;
          enabledItems[nextIndex]?.focus();
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : enabledItems.length - 1;
          enabledItems[prevIndex]?.focus();
          break;
        }
        case 'Home': {
          e.preventDefault();
          enabledItems[0]?.focus();
          break;
        }
        case 'End': {
          e.preventDefault();
          enabledItems[enabledItems.length - 1]?.focus();
          break;
        }
        /* Enter / Space — APG menu pattern: activate the currently
           focused item. Native <button> already fires onClick on Enter
           and Space, but two adopter-feedback gaps required an
           explicit handler here:

             1. Space on a button inside a long menu can scroll the
                page when the menu container itself isn't the scroll
                root (Chrome 124 regression). `preventDefault` stops
                the scroll without suppressing the click — the click
                is dispatched manually below.
             2. Composite consumers (Cabinet MultiSelect, 2026-05-21)
                wrap Menu in their own listbox-style state machine and
                expected Enter/Space to bubble through onAction +
                onClose. With the previous fall-through to `default:
                break`, those keys never reached the menu-level
                contract — the consumer's onClose ran on click only,
                making keyboard activation feel "broken" even though
                the click itself fired.

           Routing through .click() preserves the existing
           handleClick → onClick → ctxAction → ctxClose chain in
           Menu.Item, so the only behavior delta is "Space no longer
           scrolls" + "explicit keyboard parity with click". */
        case 'Enter':
        case ' ': {
          if (currentIndex < 0) break;
          e.preventDefault();
          enabledItems[currentIndex]?.click();
          break;
        }
        case 'Escape': {
          e.preventDefault();
          onClose?.('escape');
          break;
        }
        case 'Tab': {
          // Do NOT preventDefault — let browser handle natural tab order
          onClose?.('tab');
          break;
        }
        default:
          break;
      }
    },
    [onClose],
  );

  const ctxValue = React.useMemo<MenuContextValue>(
    () => ({ onAction, onClose }),
    [onAction, onClose],
  );

  return (
    <MenuContext.Provider value={ctxValue}>
      <div
        ref={ref}
        role="menu"
        aria-label={ariaLabel}
        tabIndex={-1}
        className={cx('j-menu', className)}
        style={style}
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
    </MenuContext.Provider>
  );
}) as React.ForwardRefExoticComponent<MenuProps & React.RefAttributes<HTMLDivElement>> & {
  Item: typeof MenuItem;
  Divider: typeof MenuDivider;
};

// ── Menu.Item ────────────────────────────────────────

export interface MenuItemProps {
  /** Value identifier -- passed to Menu's onAction. Should be unique within the menu. */
  value?: string;
  /** Click handler -- called when item is activated. */
  onClick?: () => void;
  /** Item label text. */
  label?: ReactNode;
  /** Optional leading icon. */
  icon?: ReactNode;
  /** Optional description below the label. */
  description?: ReactNode;
  /** Destructive styling. */
  destructive?: boolean;
  /** Disabled state. */
  disabled?: boolean;
  className?: string;
  children?: ReactNode;
}

function MenuItem({
  value,
  onClick,
  label,
  icon,
  description,
  destructive,
  disabled,
  className,
  children,
}: MenuItemProps) {
  const { onAction: ctxAction, onClose: ctxClose } = useContext(MenuContext);
  const descId = useId();
  const hasDescription = description != null;

  const handleClick = React.useCallback(() => {
    if (disabled) return;
    onClick?.();
    if (value != null) {
      ctxAction?.(value);
    }
    ctxClose?.('select');
  }, [disabled, onClick, value, ctxAction, ctxClose]);

  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      className={cx('j-menu-item', destructive && 'j-menu-item-destructive', className)}
      disabled={disabled}
      onClick={handleClick}
      aria-describedby={hasDescription ? descId : undefined}
    >
      {icon && (
        <span className="j-menu-item-icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <div>
        <div className="j-menu-item-label">{label ?? children}</div>
        {hasDescription && (
          <div id={descId} className="j-menu-item-desc">
            {description}
          </div>
        )}
      </div>
    </button>
  );
}

MenuItem.displayName = 'Menu.Item';

// ── Menu.Divider ─────────────────────────────────────

function MenuDivider() {
  return <div className="j-menu-divider" role="separator" />;
}

MenuDivider.displayName = 'Menu.Divider';

// ── Attach sub-components ────────────────────────────

Menu.Item = MenuItem;
Menu.Divider = MenuDivider;
